import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';
import { VideoData } from '@lumen5/luminary';

import { formatAsset, formatProperties } from './serialization/assetFormatters';
import { NO_MUSIC_ID, NO_VIDEO_STATE_MESSAGE } from './constants';
import {
    getCaptionsDescription,
    getSceneDescriptions,
    getSceneGroupDescriptions,
    getTemplateDescription,
    getVideoDescription,
    getVideoMusicDescription,
} from './serialization';
import type {
    AssetChanges,
    AssetDescription,
    CaptionsChanges,
    ModifiedAsset,
    ModifiedScene,
    ModifiedSceneGroup,
    MusicChanges,
    PropertyChange,
    SceneChanges,
    SceneCoverage,
    SceneDescription,
    SceneGroupChanges,
    SceneGroupDescription,
    TemplateChanges,
    TranscriptDescription,
    VideoChanges,
    VideoDiff,
    VideoStateSnapshot,
} from './types';

import type { SceneAddressing } from 'js/src/agent/tools/LintVideoTool';
import { computeSceneShortIds, defaultShortSceneId } from 'js/src/agent/tools/scene-addressing';
import {
    computeSceneGroupShortIds,
    defaultShortSceneGroupId,
} from 'js/src/agent/tools/scene-group-addressing';
import type { MediaCandidates } from 'js/src/client';

/**
 * Capture a snapshot of the current video state from Luminary.
 *
 * @param getState Optional Redux accessor, resolved once here. When provided, scenes in
 *   voice-driven groups carry `coverage` (which transcript tokens each scene owns) and the
 *   transcript includes pauses (SilenceNodes, which live in the Lexical editor rather than in
 *   Luminary). Callers without a Redux store (e.g. the storeless description path) pass none, and
 *   coverage/pauses are omitted.
 */
export async function captureVideoState(
    luminary: Luminary,
    mediaCandidates?: MediaCandidates | null,
    getState?: GetRootState,
    includeColorNames: boolean = true,
): Promise<VideoStateSnapshot> {
    // Wait for Luminary to fully load assets before capturing state, then resolve the store once
    // so coverage and pauses derive from the same post-load moment as the structure below. Reading
    // Redux before the await would pin coverage/pauses to a pre-await snapshot while the structure
    // is read post-await, leaving the two internally one tick out of sync.
    await luminary.onAssetsDoneTryingToLoad();
    const state = getState?.();

    const videoDesc = getVideoDescription(luminary, state);
    const templateDesc = getTemplateDescription(luminary, includeColorNames, state);
    const musicDesc = getVideoMusicDescription(luminary);
    const captionsDesc = getCaptionsDescription(luminary, includeColorNames);

    // Capture scenes
    const scenes: SceneDescription[] = [];
    for (const scene of luminary.scenes) {
        if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
            continue;
        }

        const sceneDesc = getSceneDescriptions(scene, luminary, state, includeColorNames, true);
        scenes.push(sceneDesc);
    }

    // Capture scene groups
    const sceneGroups = getSceneGroupDescriptions(luminary, state);

    return {
        timestamp: Date.now(),
        video: videoDesc,
        template: templateDesc,
        music: musicDesc,
        captions: captionsDesc,
        scenes,
        sceneGroups,
    };
}

/**
 * Compute the diff between two video state snapshots
 */
export function computeVideoDiff(
    prevState: VideoStateSnapshot,
    currentState: VideoStateSnapshot,
): VideoDiff {
    const diff: VideoDiff = {
        diffType: 'incremental',
        baselineTimestamp: prevState.timestamp,
        currentTimestamp: currentState.timestamp,
        changes: {
            scenes: {
                added: [],
                removed: [],
                modified: [],
            },
        },
    };

    // Check video-level changes
    const videoChanges = computeObjectDiff(prevState.video, currentState.video);
    if (Object.keys(videoChanges).length > 0) {
        diff.changes.video = videoChanges as VideoChanges;
    }

    // Check template changes
    const templateChanges = computeObjectDiff(prevState.template, currentState.template);
    if (Object.keys(templateChanges).length > 0) {
        diff.changes.template = templateChanges as TemplateChanges;
    }

    // Check music changes
    const musicChanges = computeObjectDiff(prevState.music, currentState.music);
    if (Object.keys(musicChanges).length > 0) {
        diff.changes.music = musicChanges as MusicChanges;
    }

    // Check captions changes. Either side can be absent (a video gains or loses its first
    // captionable scene group), so diff over both sides' keys: computeObjectDiff walks only
    // `current`, which would silently drop the whole block disappearing.
    const captionsChanges = computeOptionalObjectDiff(prevState.captions, currentState.captions);
    if (Object.keys(captionsChanges).length > 0) {
        diff.changes.captions = captionsChanges as CaptionsChanges;
    }

    // Compute scene changes
    const sceneChanges = computeSceneChanges(prevState.scenes, currentState.scenes);
    diff.changes.scenes = sceneChanges;

    // Compute scene group changes
    const sceneGroupChanges = computeSceneGroupChanges(
        prevState.sceneGroups,
        currentState.sceneGroups,
    );
    if (
        sceneGroupChanges.added.length > 0 ||
        sceneGroupChanges.removed.length > 0 ||
        sceneGroupChanges.modified.length > 0
    ) {
        diff.changes.sceneGroups = sceneGroupChanges;
    }

    return diff;
}

/**
 * Helper to compute property changes between two objects
 */
function computeObjectDiff<T extends Record<string, unknown>>(
    prev: T,
    current: T,
): Record<string, PropertyChange<unknown>> {
    const changes: Record<string, PropertyChange<unknown>> = {};

    for (const key in current) {
        if (prev[key] !== current[key]) {
            changes[key] = {
                from: prev[key],
                to: current[key],
            };
        }
    }

    return changes;
}

/**
 * Like computeObjectDiff, but for a section that can be absent on either side. Walks the union of
 * both key sets so a section appearing or disappearing wholesale is reported, where the plain
 * version — iterating only `current` — would report nothing when `current` is gone.
 */
function computeOptionalObjectDiff<T extends Record<string, unknown>>(
    prev: T | undefined,
    current: T | undefined,
): Record<string, PropertyChange<unknown>> {
    const changes: Record<string, PropertyChange<unknown>> = {};

    for (const key of new Set([...Object.keys(prev ?? {}), ...Object.keys(current ?? {})])) {
        const from = prev?.[key];
        const to = current?.[key];
        if (from !== to) {
            changes[key] = { from, to };
        }
    }

    return changes;
}

/**
 * Compute scene-level changes
 */
/** Stable key for matching a scene across snapshots: its id, or position when id-less. */
function sceneKey(scene: SceneDescription): string {
    return scene.id ?? `index:${scene.globalIndex}`;
}

/** Stable key for matching a scene group across snapshots: its id, or position when id-less. */
function sceneGroupKey(group: SceneGroupDescription): string {
    return group.id ?? `index:${group.index}`;
}

function computeSceneChanges(
    prevScenes: SceneDescription[],
    currentScenes: SceneDescription[],
): SceneChanges {
    const changes: SceneChanges = {
        added: [],
        removed: [],
        modified: [],
    };

    // Match scenes by stable id when available, falling back to position for
    // id-less snapshots. Matching by identity means a scene that only shifted
    // position (e.g. after a split/insert renumbered everything) is recognized as
    // the same scene, not reported as a phantom edit of whatever now sits at its
    // old index.
    const prevSceneMap = new Map(prevScenes.map((s) => [sceneKey(s), s]));
    const currentSceneMap = new Map(currentScenes.map((s) => [sceneKey(s), s]));

    // Find added scenes
    for (const scene of currentScenes) {
        if (!prevSceneMap.has(sceneKey(scene))) {
            changes.added.push(scene);
        }
    }

    // Find removed and modified scenes
    for (const prevScene of prevScenes) {
        const currentScene = currentSceneMap.get(sceneKey(prevScene));

        if (!currentScene) {
            changes.removed.push(prevScene);
        } else {
            // Check for modifications
            const sceneModifications = computeSceneModifications(prevScene, currentScene);
            if (sceneModifications) {
                changes.modified.push(sceneModifications);
            }
        }
    }

    return changes;
}

/**
 * Compute modifications within a single scene
 */
function computeSceneModifications(
    prevScene: SceneDescription,
    currentScene: SceneDescription,
): ModifiedScene | null {
    const modifications: ModifiedScene = {
        id: currentScene.id,
        globalIndex: currentScene.globalIndex,
        sceneGroupIndex: currentScene.sceneGroupIndex,
        changes: {
            assets: {
                added: [],
                removed: [],
                modified: [],
            },
        },
    };

    let hasChanges = false;

    // Check scene-level property changes
    if (prevScene.design !== currentScene.design) {
        modifications.changes.sceneDesign = {
            from: prevScene.design,
            to: currentScene.design,
        };
        hasChanges = true;
    }

    if (prevScene.duration !== currentScene.duration) {
        modifications.changes.duration = {
            from: prevScene.duration,
            to: currentScene.duration,
        };
        hasChanges = true;
    }

    if (prevScene.subscenes !== currentScene.subscenes) {
        modifications.changes.subscenes = {
            from: prevScene.subscenes,
            to: currentScene.subscenes,
        };
        hasChanges = true;
    }

    if (
        JSON.stringify(prevScene.textPositionPresets) !==
        JSON.stringify(currentScene.textPositionPresets)
    ) {
        modifications.changes.textPositionPresets = {
            from: prevScene.textPositionPresets,
            to: currentScene.textPositionPresets,
        };
        hasChanges = true;
    }

    // Coverage changes when the scene's transcript slice or its role (overlay↔base) changes. This
    // is how one add_voiceover_scene call surfaces as a shrunken base scene alongside the new overlay.
    if (JSON.stringify(prevScene.coverage) !== JSON.stringify(currentScene.coverage)) {
        modifications.changes.coverage = {
            from: prevScene.coverage,
            to: currentScene.coverage,
        };
        hasChanges = true;
    }

    // Whether the global caption track renders on this scene — the one per-scene caption fact.
    if (prevScene.captionsVisible !== currentScene.captionsVisible) {
        modifications.changes.captionsVisible = {
            from: prevScene.captionsVisible,
            to: currentScene.captionsVisible,
        };
        hasChanges = true;
    }

    // Compute asset changes
    const assetChanges = computeAssetChanges(prevScene.assets, currentScene.assets);
    modifications.changes.assets = assetChanges;

    if (
        assetChanges.added.length > 0 ||
        assetChanges.removed.length > 0 ||
        assetChanges.modified.length > 0
    ) {
        hasChanges = true;
    }

    return hasChanges ? modifications : null;
}

/**
 * Compute asset-level changes within a scene
 */
function computeAssetChanges(
    prevAssets: AssetDescription[],
    currentAssets: AssetDescription[],
): AssetChanges {
    const changes: AssetChanges = {
        added: [],
        removed: [],
        modified: [],
    };

    // Create maps for efficient lookup
    const prevAssetMap = new Map(prevAssets.map((a) => [a.name, a]));
    const currentAssetMap = new Map(currentAssets.map((a) => [a.name, a]));

    // Find added assets
    for (const asset of currentAssets) {
        if (!prevAssetMap.has(asset.name)) {
            changes.added.push(asset);
        }
    }

    // Find removed and modified assets
    for (const prevAsset of prevAssets) {
        const currentAsset = currentAssetMap.get(prevAsset.name);

        if (!currentAsset) {
            changes.removed.push(prevAsset);
        } else {
            // Check for modifications
            const assetModifications = computeAssetModifications(prevAsset, currentAsset);
            if (assetModifications) {
                changes.modified.push(assetModifications);
            }
        }
    }

    return changes;
}

/**
 * Compute modifications within a single asset
 */
function computeAssetModifications(
    prevAsset: AssetDescription,
    currentAsset: AssetDescription,
): ModifiedAsset | null {
    const propertyChanges: Record<string, PropertyChange<unknown>> = {};

    // Check basic properties
    if (prevAsset.type !== currentAsset.type) {
        propertyChanges.type = { from: prevAsset.type, to: currentAsset.type };
    }

    if (prevAsset.bounds !== currentAsset.bounds) {
        propertyChanges.bounds = { from: prevAsset.bounds, to: currentAsset.bounds };
    }

    if ('subscene' in prevAsset || 'subscene' in currentAsset) {
        const prevSubscene = 'subscene' in prevAsset ? prevAsset.subscene : undefined;
        const currentSubscene = 'subscene' in currentAsset ? currentAsset.subscene : undefined;
        if (prevSubscene !== currentSubscene) {
            propertyChanges.subscene = { from: prevSubscene, to: currentSubscene };
        }
    }

    // Check all asset-specific properties by comparing the full objects
    // We exclude 'name', 'type', 'bounds', and 'subscene' as we've already checked them
    const allKeys = new Set([...Object.keys(prevAsset), ...Object.keys(currentAsset)]);

    for (const key of allKeys) {
        // Skip already-checked properties
        if (key === 'name' || key === 'type' || key === 'bounds' || key === 'subscene') {
            continue;
        }

        const prevValue = (prevAsset as Record<string, unknown>)[key];
        const currentValue = (currentAsset as Record<string, unknown>)[key];

        // Deep comparison for objects/arrays
        if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
            propertyChanges[key] = { from: prevValue, to: currentValue };
        }
    }

    return Object.keys(propertyChanges).length > 0
        ? { name: currentAsset.name, changes: propertyChanges }
        : null;
}

/**
 * Compute scene group-level changes
 */
function computeSceneGroupChanges(
    prevGroups: SceneGroupDescription[],
    currentGroups: SceneGroupDescription[],
): SceneGroupChanges {
    const changes: SceneGroupChanges = { added: [], removed: [], modified: [] };

    // Match groups by stable id when available, falling back to position for
    // id-less snapshots. Identity matching keeps a group that only shifted index
    // (after an insert/split renumbered later groups) from reading as a content
    // edit of whatever group now occupies its old index.
    const prevMap = new Map(prevGroups.map((g) => [sceneGroupKey(g), g]));
    const currentMap = new Map(currentGroups.map((g) => [sceneGroupKey(g), g]));

    // Find added groups
    for (const group of currentGroups) {
        if (!prevMap.has(sceneGroupKey(group))) {
            changes.added.push(group);
        }
    }

    // Find removed and modified groups
    for (const prevGroup of prevGroups) {
        const currentGroup = currentMap.get(sceneGroupKey(prevGroup));
        if (!currentGroup) {
            changes.removed.push(prevGroup);
        } else {
            const modifications = computeSceneGroupModifications(prevGroup, currentGroup);
            if (modifications) {
                changes.modified.push(modifications);
            }
        }
    }

    return changes;
}

/**
 * Compute modifications within a single scene group
 */
function computeSceneGroupModifications(
    prev: SceneGroupDescription,
    current: SceneGroupDescription,
): ModifiedSceneGroup | null {
    const modifications: ModifiedSceneGroup = {
        id: current.id,
        index: current.index,
        changes: {},
    };

    let hasChanges = false;

    if (prev.type !== current.type) {
        modifications.changes.type = { from: prev.type, to: current.type };
        hasChanges = true;
    }

    if (JSON.stringify(prev.sceneIndices) !== JSON.stringify(current.sceneIndices)) {
        modifications.changes.sceneIndices = { from: prev.sceneIndices, to: current.sceneIndices };
        hasChanges = true;
    }

    if (prev.duration !== current.duration) {
        modifications.changes.duration = { from: prev.duration, to: current.duration };
        hasChanges = true;
    }

    if (JSON.stringify(prev.transcript) !== JSON.stringify(current.transcript)) {
        modifications.changes.transcript = {
            from: prev.transcript,
            to: current.transcript,
        } as PropertyChange<TranscriptDescription | null>;
        hasChanges = true;
    }

    if (prev.voice !== current.voice) {
        modifications.changes.voice = { from: prev.voice, to: current.voice };
        hasChanges = true;
    }

    if (prev.voiceSpeed !== current.voiceSpeed) {
        modifications.changes.voiceSpeed = { from: prev.voiceSpeed, to: current.voiceSpeed };
        hasChanges = true;
    }

    return hasChanges ? modifications : null;
}

/**
 * Format initial video state snapshot as a git-style diff showing all properties as additions.
 * Used to inject the starting video state into system messages. When no video
 * has been created yet (no scenes), reports that instead of an empty diff so
 * agents don't treat a not-yet-created video as if it existed.
 */
export function formatInitialStateAsGitDiff(
    snapshot: VideoStateSnapshot,
    sceneAddressing: SceneAddressing = 'index',
): string {
    if (snapshot.video.sceneCount === 0) {
        return NO_VIDEO_STATE_MESSAGE;
    }

    return formatVideoStateAsGitDiff(snapshot, sceneAddressing);
}

/**
 * Format a video state snapshot as a git-style diff showing all properties as
 * additions, unconditionally. Unlike formatInitialStateAsGitDiff, this renders
 * the video/template/music header even when the video has no scenes yet — used
 * by create_blank_video to surface the freshly-created (still empty) video's
 * template and aspect ratio so the agent can see what it's building on.
 */
export function formatVideoStateAsGitDiff(
    snapshot: VideoStateSnapshot,
    sceneAddressing: SceneAddressing = 'index',
): string {
    let diffOutput = '```diff\n';
    diffOutput += '@@ Initial Video State @@\n';

    // Video properties
    diffOutput += formatProperties(snapshot.video, '+ ');

    // Template properties
    diffOutput += '@@ Template @@\n';
    diffOutput += formatProperties(snapshot.template, '+ ');

    // Music properties
    diffOutput += '@@ Music @@\n';
    if (snapshot.music.id === NO_MUSIC_ID) {
        diffOutput += '+ music: No music set\n';
    } else {
        diffOutput += formatProperties(snapshot.music, '+ ', ['representation']);
    }

    // Captions properties. Serialized once here rather than repeated on every scene: the track is
    // global, and `set_captions` writes it across the whole video at once. Omitted entirely when
    // the video has nothing to caption, so the section's presence means captions are available.
    if (snapshot.captions) {
        diffOutput += '@@ Captions @@\n';
        if (!snapshot.captions.enabled) {
            diffOutput += '+ captions: Captions are off\n';
        } else {
            diffOutput += formatProperties(snapshot.captions, '+ ');
        }
    }

    // The stable short scene id (git-style prefix of each scene's persisted UUID) the agent
    // addresses scenes by. Computed over the whole scene set so collisions lengthen consistently.
    const shortIds = computeSceneShortIds(
        snapshot.scenes.map((scene) => scene.id).filter((id): id is string => id !== undefined),
    );

    // The stable short scene_group_id the agent addresses scene groups by (scene_id mode only).
    // Computed over the whole group set so any collision lengthens consistently.
    const groupShortIds = computeSceneGroupShortIds(
        snapshot.sceneGroups
            .map((group) => group.id)
            .filter((id): id is string => id !== undefined),
    );

    // Scenes nested under their scene group, so the transcript→scene mapping is visible at a
    // glance: each group header is followed by its scenes (with their per-scene `covers:` slice).
    const scenesByGroupIndex = bucketScenesByGroupIndex(snapshot.scenes);
    for (const group of snapshot.sceneGroups) {
        diffOutput += formatSceneGroupHeader(group, sceneAddressing, groupShortIds);
        diffOutput += formatSceneGroupProperties(group, '+ ');
        for (const scene of scenesByGroupIndex.get(group.index) ?? []) {
            diffOutput += formatFullSceneBlock(
                scene,
                scene.id ? shortIds.get(scene.id) : undefined,
                sceneAddressing,
            );
        }
        scenesByGroupIndex.delete(group.index);
    }

    // Defensively emit any scene not claimed by a group so nothing is silently dropped.
    const orphanScenes = [...scenesByGroupIndex.entries()]
        .sort(([a], [b]) => a - b)
        .flatMap(([, scenes]) => scenes);
    if (orphanScenes.length > 0) {
        diffOutput += '@@ Scenes (no scene group) @@\n';
        for (const scene of orphanScenes) {
            diffOutput += formatFullSceneBlock(
                scene,
                scene.id ? shortIds.get(scene.id) : undefined,
                sceneAddressing,
            );
        }
    }

    diffOutput += '```\n';
    return diffOutput;
}

/** Group a flat scene list by its sceneGroupIndex, preserving timeline order within each group. */
function bucketScenesByGroupIndex(scenes: SceneDescription[]): Map<number, SceneDescription[]> {
    const byGroup = new Map<number, SceneDescription[]>();
    for (const scene of scenes) {
        const list = byGroup.get(scene.sceneGroupIndex) ?? [];
        list.push(scene);
        byGroup.set(scene.sceneGroupIndex, list);
    }
    return byGroup;
}

/**
 * A scene group's section header. The group's metadata renders as child property lines below. In
 * scene_id mode the group is labelled by its stable short scene_group_id (the handle the group
 * tools address it by); in index mode by its positional index. A bare context group (unchanged,
 * owning a changed scene) carries no id in the diff, so it falls back to the index either way.
 */
function formatSceneGroupHeader(
    group: Pick<SceneGroupDescription, 'index' | 'id'>,
    sceneAddressing: SceneAddressing,
    groupShortIds: Map<string, string>,
    statusSuffix: string = '',
): string {
    const label =
        sceneAddressing === 'scene_id' && group.id
            ? (groupShortIds.get(group.id) ?? defaultShortSceneGroupId(group.id))
            : `${group.index}`;
    return `@@ Scene Group ${label}${statusSuffix} @@\n`;
}

/**
 * A scene block's section header. In scene_id mode the scene is labelled by its stable short
 * scene_id (the handle the edit tools address it by), so the positional "Scene index N" is dropped;
 * in index mode it keeps the positional header. Falls back to the index when no scene_id is known.
 */
function formatSceneBlockHeader(
    scene: { globalIndex: number },
    shortId: string | undefined,
    sceneAddressing: SceneAddressing,
    statusSuffix: string = '',
): string {
    if (sceneAddressing === 'scene_id' && shortId) {
        return `@@ Scene scene_id ${shortId}${statusSuffix} @@\n`;
    }
    return `@@ Scene index ${scene.globalIndex}${statusSuffix} @@\n`;
}

/**
 * A scene group's metadata as child property lines (parallel to how a scene renders its
 * design/duration): type, duration, transcript language, and — for AI voiceover — the voice and
 * speed. The scene membership and index aren't repeated here: the index is in the header and the
 * scenes render as nested blocks below.
 */
function formatSceneGroupProperties(group: SceneGroupDescription, prefix: string): string {
    let output = `${prefix}type: ${group.type}\n`;
    output += `${prefix}duration: ${group.duration}\n`;
    if (group.transcript) {
        output += `${prefix}language: ${group.transcript.language}\n`;
    }
    if (group.voice) {
        output += `${prefix}voice: ${group.voice}\n`;
    }
    if (group.voiceSpeed) {
        output += `${prefix}voiceSpeed: ${group.voiceSpeed}\n`;
    }
    return output;
}

const COVERAGE_ROLE_LABEL: Record<SceneCoverage['role'], string> = {
    overlay: 'overlay',
    uncovered: 'UNCOVERED — waveform base visible',
    speaker: 'speaker visible',
};

/**
 * The `covers:` line for a scene: the transcript words it owns and its role over them. Empty string
 * when the scene has no coverage (non-voicedriven groups, or storeless capture with no state).
 */
function formatCoverage(coverage: SceneCoverage | undefined, prefix: string): string {
    if (!coverage) {
        return '';
    }
    const covers = `${prefix}covers: "${coverage.words}" (${COVERAGE_ROLE_LABEL[coverage.role]})\n`;
    // A separate line from `covers`, which stays verbatim for copying. Re-timing the voiceover then
    // shows as a change to the cues alone, rather than making the covered words look different.
    return coverage.cues ? `${covers}${prefix}cues: "${coverage.cues}"\n` : covers;
}

/**
 * The `captionsVisible:` line for a scene. Empty when the field is absent — captions off, or a
 * scene outside a voice-driven group — so the line only appears where it is a fact the agent can
 * act on.
 */
function formatCaptionsVisible(captionsVisible: boolean | undefined, prefix: string): string {
    if (captionsVisible === undefined) {
        return '';
    }
    return `${prefix}captionsVisible: ${captionsVisible}\n`;
}

/** Full-state rendering of a single scene (stable id, properties, coverage, text positions, assets). */
function formatFullSceneBlock(
    scene: SceneDescription,
    shortId: string | undefined,
    sceneAddressing: SceneAddressing,
): string {
    let output = formatSceneBlockHeader(scene, shortId, sceneAddressing);
    // In index mode the header is positional, so surface the stable short scene id as a child line —
    // the handle the edit tools address the scene by (set_text/set_media `scene_id`), which stays
    // valid as other scenes are added. In scene_id mode the header already IS the scene_id, so the
    // separate line is redundant. The full `id` and `sceneGroupIndex` are for diff matching only.
    if (sceneAddressing === 'index' && shortId) {
        output += `+ scene_id: ${shortId}\n`;
    }
    output += formatProperties(scene, '+ ', [
        'id',
        'globalIndex',
        'sceneGroupIndex',
        'assets',
        'textPositionPresets',
        'coverage',
        'captionsVisible',
    ]);
    output += formatCoverage(scene.coverage, '+ ');
    output += formatCaptionsVisible(scene.captionsVisible, '+ ');
    if (scene.textPositionPresets.length > 0) {
        output += `+ textPositionPresets: [${scene.textPositionPresets.join(', ')}]\n`;
    }
    for (const asset of scene.assets) {
        output += formatAsset(asset, '+ ');
    }
    return output;
}

/**
 * Format video diff as git-style patch with color-coded changes.
 * Used for tool results and system messages.
 */
export function formatVideoDiffAsGitPatch(
    diff: VideoDiff,
    sceneIndexFilter?: number,
    sceneAddressing: SceneAddressing = 'index',
): string {
    // Check if there are any changes at all
    const hasSceneGroupChanges =
        diff.changes.sceneGroups &&
        (diff.changes.sceneGroups.added.length > 0 ||
            diff.changes.sceneGroups.removed.length > 0 ||
            diff.changes.sceneGroups.modified.length > 0);

    const hasChanges =
        diff.changes.video ||
        diff.changes.template ||
        diff.changes.music ||
        diff.changes.captions ||
        diff.changes.scenes.added.length > 0 ||
        diff.changes.scenes.removed.length > 0 ||
        diff.changes.scenes.modified.length > 0 ||
        hasSceneGroupChanges;

    if (!hasChanges) {
        return ''; // No changes, don't add anything
    }

    let diffOutput = '```diff\n';

    // Video-level changes
    if (diff.changes.video) {
        diffOutput += '@@ Video Properties @@\n';
        for (const [key, change] of Object.entries(diff.changes.video)) {
            diffOutput += `- ${key}: ${String(change.from)}\n`;
            diffOutput += `+ ${key}: ${String(change.to)}\n`;
        }
    }

    // Template changes - skip if filtering by scene
    if (diff.changes.template && sceneIndexFilter === undefined) {
        diffOutput += '@@ Template Properties @@\n';
        for (const [key, change] of Object.entries(diff.changes.template)) {
            diffOutput += `- ${key}: ${String(change.from)}\n`;
            diffOutput += `+ ${key}: ${String(change.to)}\n`;
        }
    }

    // Music changes - skip if filtering by scene
    if (diff.changes.music && sceneIndexFilter === undefined) {
        diffOutput += '@@ Music Properties @@\n';
        for (const [key, change] of Object.entries(diff.changes.music)) {
            const fromStr =
                typeof change.from === 'object'
                    ? JSON.stringify(change.from)
                    : String(change.from ?? 'null');
            const toStr =
                typeof change.to === 'object'
                    ? JSON.stringify(change.to)
                    : String(change.to ?? 'null');
            diffOutput += `- ${key}: ${fromStr}\n`;
            diffOutput += `+ ${key}: ${toStr}\n`;
        }
    }

    // Caption changes - skip if filtering by scene. Captions are global, so a per-scene diff has
    // nothing to say about them.
    if (diff.changes.captions && sceneIndexFilter === undefined) {
        diffOutput += '@@ Captions @@\n';
        for (const [key, change] of Object.entries(diff.changes.captions)) {
            diffOutput += `- ${key}: ${String(change.from ?? 'null')}\n`;
            diffOutput += `+ ${key}: ${String(change.to ?? 'null')}\n`;
        }
    }

    // Scene-group and scene changes, interleaved under one header per group so that (for example)
    // a single add_voiceover_scene — which adds an overlay scene and shrinks the neighboring base scene —
    // reads as one coherent group section. Group-level changes are skipped when filtering by scene.
    const { added, removed, modified } = diff.changes.scenes;
    const filterByScene = <T extends { globalIndex: number }>(items: T[]): T[] =>
        sceneIndexFilter === undefined
            ? items
            : items.filter((s) => s.globalIndex === sceneIndexFilter);
    const filteredAdded = filterByScene(added);
    const filteredRemoved = filterByScene(removed);
    const filteredModified = filterByScene(modified);

    const groupChanges = sceneIndexFilter === undefined ? diff.changes.sceneGroups : undefined;
    const addedGroupByIndex = new Map((groupChanges?.added ?? []).map((g) => [g.index, g]));
    const removedGroupByIndex = new Map((groupChanges?.removed ?? []).map((g) => [g.index, g]));
    const modifiedGroupByIndex = new Map((groupChanges?.modified ?? []).map((g) => [g.index, g]));

    // The short scene_group_id handle for each changed group (scene_id mode only). Computed over the
    // changed groups the diff carries — a 6-char UUID prefix is stable regardless of the subset, so
    // it matches the handle shown in the last full state read.
    const groupShortIds = computeSceneGroupShortIds(
        [
            ...(groupChanges?.added ?? []),
            ...(groupChanges?.removed ?? []),
            ...(groupChanges?.modified ?? []),
        ]
            .map((group) => group.id)
            .filter((id): id is string => id !== undefined),
    );

    // Bucket scene changes by owning group. Removed scenes carry their previous-snapshot
    // sceneGroupIndex, so they bucket under the group they were removed from.
    const addedByGroup = bucketScenesByGroupIndex(filteredAdded);
    const removedByGroup = bucketScenesByGroupIndex(filteredRemoved);
    const modifiedByGroup = new Map<number, typeof filteredModified>();
    for (const scene of filteredModified) {
        const list = modifiedByGroup.get(scene.sceneGroupIndex) ?? [];
        list.push(scene);
        modifiedByGroup.set(scene.sceneGroupIndex, list);
    }

    // The union of every group that either changed itself or owns a changed scene, in order.
    const groupIndices = new Set<number>([
        ...addedGroupByIndex.keys(),
        ...removedGroupByIndex.keys(),
        ...modifiedGroupByIndex.keys(),
        ...addedByGroup.keys(),
        ...removedByGroup.keys(),
        ...modifiedByGroup.keys(),
    ]);

    for (const index of [...groupIndices].sort((a, b) => a - b)) {
        // Group header + its metadata. Added/removed groups render the same header + property
        // lines as the full-state format (with an (Added)/(Removed) marker); the group's scenes
        // render as nested (Added)/(Removed) blocks below, so scene membership isn't repeated here.
        if (addedGroupByIndex.has(index)) {
            diffOutput += formatSceneGroupHeader(
                addedGroupByIndex.get(index)!,
                sceneAddressing,
                groupShortIds,
                ' (Added)',
            );
            diffOutput += formatSceneGroupProperties(addedGroupByIndex.get(index)!, '+ ');
        } else if (removedGroupByIndex.has(index)) {
            diffOutput += formatSceneGroupHeader(
                removedGroupByIndex.get(index)!,
                sceneAddressing,
                groupShortIds,
                ' (Removed)',
            );
            diffOutput += formatSceneGroupProperties(removedGroupByIndex.get(index)!, '- ');
        } else if (modifiedGroupByIndex.has(index)) {
            diffOutput += formatSceneGroupHeader(
                modifiedGroupByIndex.get(index)!,
                sceneAddressing,
                groupShortIds,
                ' (Modified)',
            );
            for (const [key, change] of Object.entries(modifiedGroupByIndex.get(index)!.changes)) {
                // In scene_id mode the child scene add/remove blocks already show membership, so the
                // raw sceneIndices array (positional, and redundant with those blocks) is dropped.
                if (key === 'sceneIndices' && sceneAddressing === 'scene_id') {
                    continue;
                }
                if (key === 'transcript') {
                    // Format transcript changes with full word timings so a re-transcription shows.
                    diffOutput += formatTranscript(
                        change.from as TranscriptDescription | null,
                        '- ',
                    );
                    diffOutput += formatTranscript(change.to as TranscriptDescription | null, '+ ');
                } else {
                    diffOutput += `- ${key}: ${formatPropertyValue(change.from)}\n`;
                    diffOutput += `+ ${key}: ${formatPropertyValue(change.to)}\n`;
                }
            }
        } else {
            // Unchanged group that owns a changed scene — a bare header for context.
            diffOutput += formatSceneGroupHeader({ index }, sceneAddressing, groupShortIds);
        }

        for (const scene of addedByGroup.get(index) ?? []) {
            diffOutput += formatAddedSceneBlock(scene, sceneAddressing);
        }
        for (const scene of removedByGroup.get(index) ?? []) {
            diffOutput += formatRemovedSceneBlock(scene, sceneAddressing);
        }
        for (const scene of modifiedByGroup.get(index) ?? []) {
            diffOutput += formatModifiedSceneBlock(scene, sceneAddressing);
        }
    }

    diffOutput += '```\n';
    return diffOutput;
}

/** Incremental rendering of an added scene. */
function formatAddedSceneBlock(scene: SceneDescription, sceneAddressing: SceneAddressing): string {
    const shortId = scene.id ? defaultShortSceneId(scene.id) : undefined;
    let output = formatSceneBlockHeader(scene, shortId, sceneAddressing, ' (Added)');
    // In index mode the positional header carries no id, so surface the stable handle the edit tools
    // address the scene by (set_text/set_media `scene_id`). In scene_id mode the header already IS it.
    if (sceneAddressing === 'index' && shortId) {
        output += `+ scene_id: ${shortId}\n`;
    }
    output += `+ design: ${scene.design}\n`;
    output += `+ duration: ${scene.duration}\n`;
    output += `+ subscenes: ${scene.subscenes}\n`;
    output += formatCoverage(scene.coverage, '+ ');
    output += formatCaptionsVisible(scene.captionsVisible, '+ ');
    if (scene.textPositionPresets.length > 0) {
        output += `+ textPositionPresets: [${scene.textPositionPresets.join(', ')}]\n`;
    }
    for (const asset of scene.assets) {
        output += formatAsset(asset, '+ ');
    }
    return output;
}

/** Incremental rendering of a removed scene. */
function formatRemovedSceneBlock(
    scene: SceneDescription,
    sceneAddressing: SceneAddressing,
): string {
    const shortId = scene.id ? defaultShortSceneId(scene.id) : undefined;
    let output = formatSceneBlockHeader(scene, shortId, sceneAddressing, ' (Removed)');
    output += `- design: ${scene.design}\n`;
    output += `- duration: ${scene.duration}\n`;
    output += formatCoverage(scene.coverage, '- ');
    output += formatCaptionsVisible(scene.captionsVisible, '- ');
    for (const asset of scene.assets) {
        output += formatAsset(asset, '- ');
    }
    return output;
}

/** Incremental rendering of a modified scene's property, coverage, and asset changes. */
function formatModifiedSceneBlock(scene: ModifiedScene, sceneAddressing: SceneAddressing): string {
    const shortId = scene.id ? defaultShortSceneId(scene.id) : undefined;
    let output = formatSceneBlockHeader(scene, shortId, sceneAddressing, ' (Modified)');

    const { sceneDesign, duration, subscenes, coverage, captionsVisible } = scene.changes;
    if (sceneDesign) {
        output += `- design: ${sceneDesign.from}\n`;
        output += `+ design: ${sceneDesign.to}\n`;
    }
    if (duration) {
        output += `- duration: ${duration.from}\n`;
        output += `+ duration: ${duration.to}\n`;
    }
    if (subscenes) {
        output += `- subscenes: ${subscenes.from}\n`;
        output += `+ subscenes: ${subscenes.to}\n`;
    }
    if (coverage) {
        output += formatCoverage(coverage.from, '- ');
        output += formatCoverage(coverage.to, '+ ');
    }
    if (captionsVisible) {
        output += formatCaptionsVisible(captionsVisible.from, '- ');
        output += formatCaptionsVisible(captionsVisible.to, '+ ');
    }

    const {
        added: addedAssets,
        removed: removedAssets,
        modified: modifiedAssets,
    } = scene.changes.assets;
    for (const asset of addedAssets) {
        output += formatAsset(asset, '+ ');
    }
    for (const asset of removedAssets) {
        output += formatAsset(asset, '- ');
    }
    for (const modifiedAsset of modifiedAssets) {
        output += `  asset: ${modifiedAsset.name}\n`;
        for (const [propKey, propChange] of Object.entries(modifiedAsset.changes)) {
            output += `-   ${propKey}: ${formatPropertyValue(propChange.from)}\n`;
            output += `+   ${propKey}: ${formatPropertyValue(propChange.to)}\n`;
        }
    }
    return output;
}

/**
 * Helper to format property values for display in diffs.
 *
 * Values are rendered in full — never truncated. A clipped tail (e.g. the later entries of a long
 * sceneIndices list) is content the agent can neither see nor safely restate. Guarding against a
 * pathologically large state happens where the state is injected (see isContextMessageOversized),
 * not by silently dropping tails here.
 */
function formatPropertyValue(value: unknown): string {
    if (value === null) {
        return 'null';
    }
    if (value === undefined) {
        return 'undefined';
    }
    if (typeof value === 'string') {
        return `"${value}"`;
    }
    const jsonStr = JSON.stringify(value);
    // JSON.stringify can return undefined for certain values (like functions, undefined)
    if (jsonStr === undefined) {
        return 'undefined';
    }
    return jsonStr;
}

/**
 * Format a transcript for display in diffs - shows full word timings
 */
function formatTranscript(transcript: TranscriptDescription | null, prefix: string): string {
    if (!transcript) {
        return `${prefix}transcript: null\n`;
    }

    let output = `${prefix}transcript:\n`;
    output += `${prefix}  language: ${transcript.language}\n`;
    output += `${prefix}  words:\n`;
    for (const word of transcript.words) {
        if (word.isPause) {
            const seconds = ((word.endTime - word.startTime) / 1000).toFixed(1);
            output += `${prefix}    <pause seconds="${seconds}"/>\n`;
        } else {
            output += `${prefix}    ${word.word}\n`;
        }
    }
    return output;
}
